<template>
  <section class="success-screen-nudge">
    <!--
      Two modes based on whether the order has any pending accounts:
        - hasPending → "Pending accounts" title + custom input + pending
          list only (no generic suggestions, no CTA per product call).
          Adding via input persists to the pending-accounts API.
        - else      → original generic-suggestions experience: input,
          "Or select…" divider, locale suggestion rows, and the CTA matrix.
    -->
    <!--
      Hold the UI until the pending list is fetched. Without this the
      default-suggestions branch (hasPending=false) flashes first and then
      snaps to pending mode once the fetch resolves.
    -->
    <Loader v-if="loading" />

    <template v-else-if="hasPending">
      <!--
        Pending mode (the pending variant). Layout order:
          1. Title + "View all requests" link
          2. "Add custom name" input — sits up here so a fresh add lands
             at the top of the list immediately below it.
          3. Up to 2 most-recently-added pending rows, newest first.
        The full list (with delete affordance) lives on the dedicated
        /pending-accounts screen, reachable via "View all requests".
      -->
      <div class="success-screen-nudge__pending-header">
        <h3 class="success-screen-nudge__title">
          {{ $t("emailAccounts.successScreen.pending.heading") }}
        </h3>
        <a
          v-if="showViewAllPending"
          href="#"
          class="success-screen-nudge__view-all"
          @click.prevent="onReviewPending"
        >
          {{ $t("emailAccounts.successScreen.pending.viewAll") }}
        </a>
      </div>

      <div class="success-screen-nudge__custom">
        <label class="success-screen-nudge__label">
          {{ $t("emailAccounts.nudge.orAddCustom") }}
        </label>
        <div class="success-screen-nudge__custom-row">
          <div class="input-addon-wrapper">
            <input
              type="text"
              class="input"
              v-model.trim="customUsername"
              :placeholder="$t('emailAccounts.nudge.customPlaceholder')"
              @input="customUsernameError = ''"
              @keydown.enter.prevent="addCustomToPending"
            />
            <span class="addon" :title="domainName">@{{ domainName }}</span>
          </div>
          <button
            type="button"
            class="button button__line-blue success-screen-nudge__add-btn"
            :disabled="!customUsername.length"
            @click="addCustomToPending"
          >
            {{ $t("emailAccounts.nudge.addButton") }}
          </button>
        </div>
        <div v-if="customUsernameError" class="error success-screen-nudge__custom-error">
          {{ customUsernameError }}
        </div>
      </div>

      <!--
        One list = real pending rows followed by generic backfill suggestions
        (spec 3: pending first, fill to 3). Both share the same email +
        Create/Buy markup; backfill rows simply carry no "added by" sub-line.
      -->
      <ul class="success-screen-nudge__rows">
        <li v-for="row in previewRows" :key="row.email" class="pending-row">
          <div class="pending-row__info">
            <span class="pending-row__email">
              {{ row.email }}
              <span v-if="row.popular" class="suggestion-row__badge" aria-label="popular">
                {{ $t("emailAccounts.nudge.popularBadge") }}
              </span>
            </span>
            <span v-if="row.subLabel" class="pending-row__sub">{{ row.subLabel }}</span>
          </div>
          <button
            type="button"
            class="button button__line-blue pending-row__action"
            @click="isBuyAction ? onBuyPending() : onCreatePreview(row.username)"
          >
            {{
              isBuyAction
                ? $t("emailAccounts.successScreen.pending.buyAction")
                : $t("emailAccounts.successScreen.pending.createAction")
            }}
          </button>
        </li>
      </ul>
    </template>

    <template v-else>
      <h3 class="success-screen-nudge__title">
        {{ $t("emailAccounts.nudge.heading") }}
      </h3>

      <!--
        Custom username input. Enter or the Add button queues
        an arbitrary username locally; CTA below resolves into Create or
        Buy depending on available licenses.
      -->
      <div class="success-screen-nudge__custom">
        <label class="success-screen-nudge__label">
          {{ $t("emailAccounts.nudge.customLabel") }}
        </label>
        <div class="success-screen-nudge__custom-row">
          <div class="input-addon-wrapper">
            <input
              type="text"
              class="input"
              v-model.trim="customUsername"
              :placeholder="$t('emailAccounts.nudge.customPlaceholder')"
              @input="customUsernameError = ''"
              @keydown.enter.prevent="addCustomUsername"
            />
            <span class="addon" :title="domainName">@{{ domainName }}</span>
          </div>
          <button
            type="button"
            class="button button__line-blue success-screen-nudge__add-btn"
            :disabled="!customUsername.length"
            @click="addCustomUsername"
          >
            {{ $t("emailAccounts.nudge.addButton") }}
          </button>
        </div>
        <div v-if="customUsernameError" class="error success-screen-nudge__custom-error">
          {{ customUsernameError }}
        </div>
      </div>

      <!-- Hidden once every suggestion is exhausted (all created) — there's
           nothing left to "select", so the heading drops too. -->
      <p v-if="displayRows.length" class="success-screen-nudge__divider">
        {{ $t("emailAccounts.nudge.orSelect") }}
      </p>

      <ul class="success-screen-nudge__rows">
        <li v-for="row in displayRows" :key="row.username" class="suggestion-row">
          <span class="suggestion-row__email">
            <strong>{{ row.username }}</strong
            >@{{ domainName }}
          </span>
          <span v-if="row.popular" class="suggestion-row__badge" aria-label="popular">
            {{ $t("emailAccounts.nudge.popularBadge") }}
          </span>
          <span class="suggestion-row__spacer" />
          <span class="suggestion-row__action-slot">
            <template v-if="isSelected(row.username)">
              <span class="suggestion-row__added">
                <span class="suggestion-row__check" aria-hidden="true">✓</span>
                {{ $t("emailAccounts.nudge.addedLabel") }}
              </span>
              <button
                type="button"
                class="suggestion-row__remove"
                :aria-label="$t('utility.remove')"
                @click="removeUsername(row.username)"
              >
                <img src="@/assets/delete.svg" alt="" />
              </button>
            </template>
            <button
              v-else
              type="button"
              class="button button__line-blue suggestion-row__add-btn"
              @click="addUsername(row.username)"
            >
              {{ $t("emailAccounts.nudge.addButton") }}
            </button>
          </span>
        </li>
      </ul>

      <div v-if="cta" class="success-screen-nudge__cta">
        <button type="button" class="button" @click="onCtaClick">
          {{ $t(cta.labelKey, { n: cta.count }) }}
        </button>
        <p v-if="cta.subNoteKey" class="success-screen-nudge__sub-note">
          {{ $t(cta.subNoteKey) }}
        </p>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import { getSuggestions } from "@/helpers/mailboxSuggestions";
import type { TMailboxSuggestion } from "@/helpers/mailboxSuggestions";
import { getNudgeCtaDescriptor, shouldOfferBuy } from "@/helpers/mailboxNudge";
import type { TNudgeCtaDescriptor } from "@/helpers/mailboxNudge";
import { getUserNameFromEmail } from "@/helpers/email";
import { REGX } from "@/helpers/const";
import pendingAccountsService from "@/services/pending-accounts/pending-accounts.service";
import type { TPendingAccountGroup } from "@/store/modules/mail/mailbox-nudge.store";
import Logger from "@/helpers/logger";
import Loader from "@/components/Utility/Loader.vue";
import {
  trackNudgeShown,
  trackNudgeAddClicked,
  trackNudgeRemoveClicked,
  trackNudgeContinued,
} from "@/telemetry/mailboxNudgeEvents";
import type { TMailboxAddType, TNudgeType } from "@/telemetry/mailboxNudgeEvents";

/**
 * A row in the pending-mode preview — either a real pending account or a
 * generic backfill suggestion. Both render identically (email + Create/Buy);
 * `subLabel` is the "added by" line for pending entries and empty for backfill.
 */
type TPreviewRow = {
  email: string;
  username: string;
  subLabel: string;
  popular: boolean;
};

/** Pending-mode preview always offers up to this many actionable rows (spec 3). */
const PENDING_PREVIEW_LIMIT = 3;

/**
 * Success-screen nudge.
 *
 * Two modes:
 *  - hasPending (order has any "Requested" rows): show a "Pending email
 *    accounts" header + custom input + the pending list. Adding via the
 *    input goes straight to the pending-accounts API (via
 *    pendingAccountsService). No generic suggestions
 *    and no CTA in this mode.
 *  - default: original layout — input, "Or select…", locale-driven
 *    suggestions, CTA matrix from `getNudgeCtaDescriptor`.
 *
 * Events (default-mode CTA):
 *  - `create(usernames: string[])` — host re-opens Create with the
 *    first pre-filled; the rest stay on the `pendingAccounts` list
 *    (already persisted there by this component on Add).
 *  - `buy(count: number)` — host routes through the buy flow.
 *  - `review-pending()` — host routes to /email-accounts/pending-accounts.
 */
export default Vue.extend({
  name: "SuccessScreenNudge",
  components: { Loader },
  data() {
    return {
      // True until the pending list is fetched — gates the template so the
      // mode (pending vs default suggestions) is only rendered once known.
      loading: true,
      selectedUsernames: [] as string[],
      customUsername: "",
      // Inline error under the custom-name input (same rules as
      // the Create modal's email field, no new rules).
      customUsernameError: "",
      // Snapshot of pending-mode at mount. Persisting an Add to pending
      // accounts mid-flow must NOT flip the template branch on this
      // view — the user only sees the pending layout on their next visit.
      hasPending: false,
      // Ordered list of pending emails currently rendered in pending
      // mode (newest first). Seeded in `created()` with up to 3 most
      // recent entries; `addCustomToPending` prepends to it. We keep
      // this as a separate list rather than re-deriving from
      // pendingAccountGroups on every change so adds during this view
      // don't kick the originally-visible rows off the list.
      visiblePendingEmails: [] as string[],
      // Generic suggestions backfilling the pending preview up to 3 rows
      // (spec 3: pending first, then generics). Snapshotted in `created()`
      // alongside `visiblePendingEmails` so the preview stays stable while the
      // user acts on it. Consumers read the `backfillSuggestions` computed,
      // which re-filters this snapshot against the live mailbox list.
      backfillSnapshot: [] as TMailboxSuggestion[],
    };
  },
  computed: {
    // Object form (not `mapGetters([...])`) — the array overload types as a
    // string-indexed map, so TS can't see the keys on `this`.
    ...mapGetters({
      locale: "locale",
      currentDomainMailboxDetails: "currentDomainMailboxDetails",
      domainName: "domain/domainName",
      pendingAccountGroups: "mail/mailboxNudge/pendingAccountGroups",
      availableLicenses: "mail/availableLicenses",
      canBuyMoreAccounts: "mail/canBuyMoreAccounts",
    }),
    existingUsernames(): string[] {
      return (this.currentDomainMailboxDetails || []).map((a: { email: string }) =>
        getUserNameFromEmail(a.email)
      );
    },
    /**
     * The backfill snapshot minus any username whose mailbox now exists.
     * The snapshot is taken in `created()`, which runs before the
     * post-create mailbox-list refresh lands — so without this reactive
     * pass a just-created mailbox (e.g. info@) would keep being suggested
     * on its own success screen. Only ever removes rows; the snapshot's
     * picks and order stay stable while the user acts on them. How many of
     * these actually render is capped in `previewRows`.
     */
    backfillSuggestions(): TMailboxSuggestion[] {
      const existing = new Set(this.existingUsernames.map(u => u.toLowerCase()));
      return this.backfillSnapshot.filter(s => !existing.has(s.username.toLowerCase()));
    },
    /**
     * Pending rows currently rendered on this view. Driven by the
     * `visiblePendingEmails` list — seeded at mount with the 2 most
     * recent entries, then prepended to on each Add. We resolve each
     * email back to its full group via `pendingAccountGroups` so the
     * "added by" line stays in sync if the store updates (e.g. a team
     * requester is appended to an existing group).
     *
     * The full list (with delete) lives on the dedicated
     * /pending-accounts screen via "View all requests".
     */
    recentPendingGroups(): TPendingAccountGroup[] {
      const byEmail = new Map<string, TPendingAccountGroup>(
        this.pendingAccountGroups.map((g: TPendingAccountGroup) => [g.suggestionEmail, g])
      );
      return this.visiblePendingEmails
        .map(email => byEmail.get(email))
        .filter(Boolean) as TPendingAccountGroup[];
    },
    /**
     * Unified pending-mode list the template renders in a single loop: the
     * real pending rows first, then the generic backfill suggestions, both
     * flattened to `{ email, username, subLabel }`. Keeps the row markup and
     * the Create/Buy hand-off in one place instead of two near-identical loops.
     */
    previewRows(): TPreviewRow[] {
      const pending = this.recentPendingGroups.map((g: TPendingAccountGroup) => ({
        email: g.suggestionEmail,
        username: getUserNameFromEmail(g.suggestionEmail),
        subLabel: this.addedByLabel(g),
        popular: false,
      }));
      // Drop any backfill whose email a pending row already occupies: the
      // user can queue a username the preview is currently suggesting (e.g.
      // type "admin" while admin@ shows as a generic), and rendering both
      // yields duplicate `:key="row.email"` values, which crashes Vue's
      // patch. The real "added by you" pending row wins.
      const pendingEmails = new Set(pending.map(r => r.email.toLowerCase()));
      const backfill = this.backfillSuggestions
        .map(s => ({
          email: `${s.username}@${this.domainName}`,
          username: s.username,
          subLabel: "",
          popular: s.popular,
        }))
        .filter(r => !pendingEmails.has(r.email.toLowerCase()));
      // The snapshot carries one spare generic beyond the free slots at
      // mount, so when a pending row drops out (its mailbox got created)
      // the spare slides in and the preview stays at 3. The cap keeps the
      // spare hidden until then.
      return [...pending, ...backfill].slice(0, PENDING_PREVIEW_LIMIT);
    },
    /**
     * Locale-driven suggestion rows for the default mode — 6 per spec §3.1
     * (skipping any usernames already created). Pending mode replaces this
     * list entirely, so the "fewer when pending" rule doesn't apply here.
     */
    suggestionRows(): TMailboxSuggestion[] {
      return getSuggestions({
        locale: this.locale,
        existingUsernames: this.existingUsernames,
        count: 6,
      });
    },
    displayRows(): TMailboxSuggestion[] {
      const knownNames = new Set(this.suggestionRows.map(r => r.username));
      const customRows = this.selectedUsernames
        .filter(u => !knownNames.has(u))
        .map(username => ({ username, popular: false }));
      return [...this.suggestionRows, ...customRows];
    },
    cta(): TNudgeCtaDescriptor | null {
      return getNudgeCtaDescriptor({
        accountsInList: this.selectedUsernames.length,
        availableLicenses: this.availableLicenses,
        canBuyMoreAccounts: this.canBuyMoreAccounts,
      });
    },
    /**
     * Whether the per-row CTA reads Buy rather than Create — no licenses left
     * AND a buy path available for this partner/plan. Where there isn't one,
     * Create stands and CreateEmail hands the click to the listing's create
     * gate, which shows the used-license screen.
     */
    isBuyAction(): boolean {
      return shouldOfferBuy({
        availableLicenses: this.availableLicenses,
        canBuyMoreAccounts: this.canBuyMoreAccounts,
      });
    },
    /**
     * "View all pending requests" link only shows when there are more than 3
     * pending requests (MWDC-6354) — with 3 or fewer the preview already
     * lists them all, so there's nothing more to view.
     */
    showViewAllPending(): boolean {
      return this.pendingAccountGroups.length > 3;
    },
    /** Analytics: which nudge variant this view renders. */
    nudgeType(): TNudgeType {
      return this.hasPending ? "pending mailbox nudge" : "generic mailbox nudge";
    },
    /**
     * Analytics: the usernames this nudge surfaced — the suggestion grid in
     * generic mode, the visible pending preview in pending mode.
     */
    suggestedMailboxes(): string[] {
      if (this.hasPending) {
        return this.visiblePendingEmails.map(email => getUserNameFromEmail(email));
      }
      return this.suggestionRows.map(r => r.username);
    },
  },
  created() {
    // Hydrate pending list from mock/storage, then snapshot whether the
    // order has any pending entries — that snapshot drives the layout for
    // the lifetime of this view. Per-row Add persists to the pending API
    // (so the next visit lands in pending mode) but must not re-flip the
    // current screen out of the default suggestion grid.
    pendingAccountsService
      .fetchPendingAccounts()
      .then(() => {
        this.hasPending = this.pendingAccountGroups.length > 0;
        // Seed the visible window with up to the 3 most recently added
        // entries, newest first. Adds during this view will prepend to this
        // list without dropping these seed rows.
        this.visiblePendingEmails = [...this.pendingAccountGroups]
          .slice(-PENDING_PREVIEW_LIMIT)
          .reverse()
          .map(g => g.suggestionEmail);
        // Backfill with generic suggestions so the section always offers up
        // to 3 actionable rows (spec 3), plus one spare beyond the free
        // slots: a pending row can drop out later (its mailbox got created)
        // and the spare fills the freed slot — `previewRows` caps what's
        // actually shown. Exclude already-created mailboxes and every
        // pending username so a generic never duplicates a pending row.
        const remaining = PENDING_PREVIEW_LIMIT - this.visiblePendingEmails.length;
        if (this.hasPending) {
          const pendingUsernames = (this.pendingAccountGroups as TPendingAccountGroup[]).map(g =>
            getUserNameFromEmail(g.suggestionEmail)
          );
          this.backfillSnapshot = getSuggestions({
            locale: this.locale,
            existingUsernames: [...this.existingUsernames, ...pendingUsernames],
            count: remaining + 1,
          });
        }
      })
      .catch(() => {
        // On failure fall back to the default-suggestions experience
        // (hasPending stays false) rather than blocking on the loader.
      })
      .finally(() => {
        this.loading = false;
        // The nudge is on screen now that the mode is settled.
        trackNudgeShown({
          nudgeType: this.nudgeType,
          suggestedMailboxes: this.suggestedMailboxes,
        });
      });
  },
  methods: {
    /**
     * The custom field follows the Create modal's email-field
     * rules — the composed `username@domain` must be a valid address
     * (REGX.email, same pattern and error copy the modal uses). Returns
     * the trimmed username, or null (with inline error set) when invalid.
     */
    validateCustomUsername(): string | null {
      const value = this.customUsername.trim();
      if (!value) return null;
      if (!REGX.email.test(`${value}@${this.domainName}`)) {
        this.customUsernameError = String(this.$t("emailAccounts.createEmail.invalidEmail"));
        return null;
      }
      const lower = value.toLowerCase();
      // Already a created mailbox on this domain — the free-form input can
      // reach names the filtered suggestion list never offers.
      if (this.existingUsernames.some(u => u.toLowerCase() === lower)) {
        this.customUsernameError = String(this.$t("emailAccounts.nudge.duplicateExists"));
        return null;
      }
      // Already queued in this nudge — the pending list (pending mode) or the
      // selected suggestions (default mode).
      const alreadyQueued = this.hasPending
        ? this.pendingAccountGroups.some(
            (g: TPendingAccountGroup) =>
              getUserNameFromEmail(g.suggestionEmail).toLowerCase() === lower
          )
        : this.selectedUsernames.some(u => u.toLowerCase() === lower);
      if (alreadyQueued) {
        this.customUsernameError = String(this.$t("emailAccounts.nudge.duplicateQueued"));
        return null;
      }
      this.customUsernameError = "";
      return value;
    },

    // ---- pending-mode helpers ----
    addedByLabel(group: TPendingAccountGroup): string {
      // Admin-added with no team requesters → "added by you". Otherwise
      // surface the first team requester's name (with "+N" for the rest);
      // the dedicated /pending-accounts screen handles the full list.
      const team = group.requesters
        .filter(r => !r.isAdminAdded)
        .map(r => r.name || r.email || "")
        .filter(Boolean);
      if (team.length === 0) {
        return String(this.$t("emailAccounts.successScreen.pending.addedByYou"));
      }
      if (team.length === 1) {
        return String(this.$t("emailAccounts.successScreen.pending.addedBy", { name: team[0] }));
      }
      return String(
        this.$t("emailAccounts.successScreen.pending.addedByMany", {
          name: team[0],
          n: team.length - 1,
        })
      );
    },

    // ---- pending mode actions ----
    addCustomToPending(): void {
      const value = this.validateCustomUsername();
      if (!value) return;
      const email = `${value}@${this.domainName}`;
      trackNudgeAddClicked({
        nudgeType: this.nudgeType,
        suggestedMailboxes: this.suggestedMailboxes,
        addedMailboxUsername: value,
        addedMailboxType: "custom",
      });
      pendingAccountsService
        .addPendingAccount(email)
        .catch(e => Logger.error("SuccessScreenNudge: addPendingAccount failed", e));
      // Prepend to the visible window so the new entry shows at the
      // top without bumping the seed rows that were already on screen.
      if (!this.visiblePendingEmails.includes(email)) {
        this.visiblePendingEmails = [email, ...this.visiblePendingEmails];
      }
      this.customUsername = "";
    },
    // Per-row Create/Buy are individual-mailbox actions — the
    // add_mailbox_nudge_continued event covers only the nudge's MAIN CTAs
    // (the generic-mode CTA button and "View all requests").
    // Create a preview row (pending entry or backfill suggestion alike) — hands
    // the username to the host's create flow.
    onCreatePreview(username: string): void {
      this.$emit("create", [username]);
    },
    onBuyPending(): void {
      this.$emit("buy", 1);
    },
    onReviewPending(): void {
      // Pending mode: the pending list IS the user's added mailboxes —
      // no separate session state needed.
      const pendingUsernames = (this.pendingAccountGroups as TPendingAccountGroup[]).map(g =>
        getUserNameFromEmail(g.suggestionEmail)
      );
      trackNudgeContinued({
        nudgeType: this.nudgeType,
        action: "View all pending requests",
        mailboxesAddedCount: pendingUsernames.length,
        mailboxesAddedUsername: pendingUsernames,
      });
      this.$emit("review-pending");
    },

    // ---- default-mode actions (unchanged) ----
    isSelected(username: string): boolean {
      return this.selectedUsernames.includes(username);
    },
    addUsername(username: string, addType: TMailboxAddType = "suggested"): void {
      if (!username) return;
      if (this.isSelected(username)) return;
      trackNudgeAddClicked({
        nudgeType: this.nudgeType,
        suggestedMailboxes: this.suggestedMailboxes,
        addedMailboxUsername: username,
        addedMailboxType: addType,
      });
      this.selectedUsernames.push(username);
      // Persist to the pending-accounts API so the next visit surfaces
      // this name in the pending list. The current view stays in default
      // mode because `hasPending` is snapshotted at mount.
      pendingAccountsService
        .addPendingAccount(`${username}@${this.domainName}`)
        .catch(e => Logger.error("SuccessScreenNudge: addPendingAccount failed", e));
    },
    removeUsername(username: string): void {
      trackNudgeRemoveClicked({
        nudgeType: this.nudgeType,
        suggestedMailboxes: this.suggestedMailboxes,
        removedMailboxUsername: username,
        removedMailboxType: this.suggestionRows.some(r => r.username === username)
          ? "suggested"
          : "custom",
      });
      this.selectedUsernames = this.selectedUsernames.filter(u => u !== username);
      pendingAccountsService
        .removePendingAccount(`${username}@${this.domainName}`)
        .catch(e => Logger.error("SuccessScreenNudge: removePendingAccount failed", e));
    },
    addCustomUsername(): void {
      const value = this.validateCustomUsername();
      if (!value) return;
      this.addUsername(value, "custom");
      this.customUsername = "";
    },
    onCtaClick(): void {
      if (!this.cta) return;
      trackNudgeContinued({
        nudgeType: this.nudgeType,
        action: this.cta.action === "create" ? "Create account" : "Buy account",
        mailboxesAddedCount: this.selectedUsernames.length,
        mailboxesAddedUsername: [...this.selectedUsernames],
      });
      if (this.cta.action === "create") {
        // Persist each queued username to the pending-accounts list
        // (the single source of truth) so a refresh mid-create-loop
        // doesn't lose the queue. CreateEmail removes them one at a
        // time via `removePendingAccount` as it successfully creates.
        this.selectedUsernames.forEach(username => {
          pendingAccountsService
            .addPendingAccount(`${username}@${this.domainName}`)
            .catch(e => Logger.error("SuccessScreenNudge: addPendingAccount failed", e));
        });
        this.$emit("create", [...this.selectedUsernames]);
      } else {
        this.$emit("buy", this.cta.count);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.success-screen-nudge {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--lineSepaCol);

  &__title {
    // Match the success-screen "…is ready" heading (.success-banner__title,
    // 22px/700) so both titles read at the same size (MWDC-6353). Applies to
    // the generic and pending nudge titles alike (shared class).
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 24px;
    color: var(--primaryTextCol);
  }

  &__label {
    display: block;
    font-size: 14px;
    color: var(--primaryTextCol);
    margin-bottom: 8px;
  }

  &__custom {
    margin-bottom: 16px;
  }

  &__custom-error {
    margin-top: 6px;
    font-size: 12px;
  }

  // Width cap shared by the custom-row, the suggestion list, and the CTA
  // below. Keeps the right-edge "Add" button on every suggestion row
  // aligned vertically with the top "Add" button next to the custom-name
  // input (560px input + 12px gap + 100px button = 672px content column).
  &__custom-row,
  &__rows,
  &__cta {
    max-width: 672px;
  }

  &__custom-row {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: space-between;

    .input-addon-wrapper {
      flex: 1;
      max-width: 560px;
    }
  }

  &__add-btn {
    min-width: 90px;
  }

  &__divider {
    color: var(--secondaryTextCol);
    margin: 16px 0 8px 0;
    font-size: 14px;
  }

  &__rows {
    list-style: none;
    padding: 0;
    margin: 0 0 24px 0;
  }

  &__cta {
    margin-top: 24px;
  }

  &__sub-note {
    margin-top: 8px;
    font-size: 12px;
    color: var(--secondaryTextCol);
  }

  &__pending-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 672px;
    margin-bottom: 16px;

    .success-screen-nudge__title {
      margin-bottom: 0;
    }
  }

  &__view-all {
    font-size: 14px;
    font-weight: 600;
    color: var(--linkBtnCol);
    text-decoration: none;
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
}

// Pending-mode row: stacked email + "added by" line on the left, single
// Create/Buy button on the right. No min-height lock — there's no
// in-row state swap to defend against (unlike .suggestion-row).
.pending-row {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid var(--lineSepaCol);

  &:last-child {
    border-bottom: none;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__email {
    color: var(--primaryTextCol);
    font-size: 14px;
    font-weight: 600;
    word-break: break-all;
  }

  &__sub {
    color: var(--secondaryTextCol);
    font-size: 12px;
  }

  &__action {
    min-width: 90px;
    margin-left: 12px;
  }
}

.suggestion-row {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid var(--lineSepaCol);
  // Lock row height to the [Add]-button state so toggling to the
  // [✓ Added + remove] state (text + 16px icon button — shorter than the
  // button) doesn't collapse the row and bump every row below upward.
  min-height: 72px;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }

  &__action-slot {
    display: flex;
  }

  &__email {
    color: var(--primaryTextCol);
    font-size: 14px;
    strong {
      font-weight: 700;
    }
  }

  &__badge {
    // Inline POPULAR pill — orange text on a faint background.
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #d97706;
    background: rgba(217, 119, 6, 0.12);
    padding: 2px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    margin-left: 8px;
  }

  &__spacer {
    flex: 1;
  }

  &__added {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--secondaryTextCol);
    font-size: 14px;
  }

  &__check {
    font-weight: 700;
    color: inherit;
  }

  &__remove {
    background: transparent;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--secondaryTextCol);
    padding: 0;
  }

  &__add-btn {
    min-width: 90px;
  }
}
</style>
